/** @jsxImportSource @emotion/react */
import { css, keyframes } from '@emotion/react';
import { withMyTheme } from '../../theme/theme';
import { useInView } from 'react-intersection-observer';
import { useRef, useEffect, useState } from 'react';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { portfolioImages } from './Portfolio';

const ScrollContainerStyle = withMyTheme(() => css`
    position: relative;
    overflow: hidden;
`);

const ScrollerStyle = css`
    display: flex;
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x mandatory;
    -webkit-scroll-snap-type: x mandatory;
    -moz-scroll-snap-type: x mandatory;
    -ms-scroll-snap-type: x mandatory;
    gap: 2vw;
    padding: 3vh 5vw;
    margin-bottom: 3vh;
    scrollbar-width: none;
    -ms-overflow-style: none;
    -webkit-overflow-scrolling: touch;
    
    &::-webkit-scrollbar {
        display: none;
    }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const DesktopImageStyle = (theme: any, isVisible = false, index = 0) => css`
    width: 30vw;
    height: 65vh;
    object-fit: cover;
    border-radius: 16px;
    flex-shrink: 0;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    scroll-snap-align: center;
    scroll-snap-stop: always;
    
    /* Animation styles */
    opacity: ${isVisible ? 1 : 0};
    transform: ${isVisible ? 'scale(1)' : 'translateY(30px)'};
    transition: opacity 0.8s ease-out, transform 0.8s ease-out, box-shadow 0.3s ease;
    transition-delay: ${0.1 + (index * 0.1)}s;
    will-change: opacity, transform;
    cursor: pointer;
    
    &:hover {
        transform: scale(1.03);
        box-shadow: 0 16px 32px rgba(0, 0, 0, 0.2);
    }
`;

const IndicatorContainerStyle = css`
    display: flex;
    justify-content: center;
    gap: 8px;
    margin-top: 20px;
`;

const IndicatorStyle = withMyTheme((theme, active: boolean) => css`
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: ${active ? theme.palette.primary.main : theme.palette.divider};
    transition: background-color 0.3s ease, transform 0.2s ease;
    cursor: pointer;
    
    &:hover {
        transform: scale(1.2);
    }
`);

const NavigationButtonStyle = withMyTheme((theme, direction: 'left' | 'right') => css`
    position: absolute;
    top: 50%;
    ${direction === 'left' ? 'left: 3vw;' : 'right: 3vw;'}
    transform: translateY(-50%);
    width: 50px;
    height: 50px;
    background-color: ${theme.palette.background.paper};
    border-radius: 12px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    z-index: 10;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transition: background-color 0.3s ease, transform 0.2s ease;
    
    &:hover {
        background-color: ${theme.palette.primary.main};
        color: ${theme.palette.primary.contrastText};
        transform: translateY(-50%) scale(1.05);
    }
    
    & svg {
        font-size: 24px;
    }
`);

export const PortfolioDesktop = () => {
    const { ref: containerRef, inView } = useInView({
        threshold: 0.1,
        triggerOnce: true
    });
    
    const scrollerRef = useRef<HTMLDivElement>(null);
    const [activeIndex, setActiveIndex] = useState(0);

    const handleScroll = () => {
        if (scrollerRef.current && portfolioImages.length > 0) {
            const containerWidth = scrollerRef.current.clientWidth;
            const centerPoint = scrollerRef.current.scrollLeft + (containerWidth / 2);
            
            // Find which element is closest to the center
            let closestIndex = 0;
            let closestDistance = Number.MAX_VALUE;
            
            for (let i = 0; i < scrollerRef.current.children.length; i++) {
                const element = scrollerRef.current.children[i] as HTMLElement;
                const elementCenter = element.offsetLeft + (element.offsetWidth / 2);
                const distance = Math.abs(centerPoint - elementCenter);
                
                if (distance < closestDistance) {
                    closestDistance = distance;
                    closestIndex = i;
                }
            }
            
            setActiveIndex(closestIndex);
        }
    };

    const scrollToIndex = (index: number) => {
        if (scrollerRef.current && scrollerRef.current.children[index]) {
            // Get the target element
            const targetElement = scrollerRef.current.children[index] as HTMLElement;
            
            // Calculate the scroll position to center the element
            const containerWidth = scrollerRef.current.clientWidth;
            const elementWidth = targetElement.offsetWidth;
            const elementLeft = targetElement.offsetLeft;
            
            // Center the element in the viewport
            const scrollPosition = elementLeft - (containerWidth / 2) + (elementWidth / 2);
            
            scrollerRef.current.scrollTo({
                left: scrollPosition,
                behavior: 'smooth'
            });
            
            // Update active index
            setActiveIndex(index);
        }
    };
    
    const handlePrevious = () => {
        const newIndex = Math.max(0, activeIndex - 1);
        scrollToIndex(newIndex);
    };
    
    const handleNext = () => {
        const newIndex = Math.min(portfolioImages.length - 1, activeIndex + 1);
        scrollToIndex(newIndex);
    };

    useEffect(() => {
        const scroller = scrollerRef.current;
        if (scroller) {
            // Add scroll event listener
            scroller.addEventListener('scroll', handleScroll);
            
            // Initial scroll to second item
            setTimeout(() => {
                scrollToIndex(1);
            }, 500);
            
            return () => scroller.removeEventListener('scroll', handleScroll);
        }
    }, []);

    return (
        <div css={ScrollContainerStyle} ref={containerRef}>
            <div css={ScrollerStyle} ref={scrollerRef}>
                {portfolioImages.map((image, index) => (
                    <img
                        key={index}
                        src={image.src}
                        alt={image.alt}
                        width={image.width}
                        height={image.height}
                        css={(theme) => DesktopImageStyle(theme, inView, index)}
                    />
                ))}
            </div>
            
            {/* Navigation Buttons */}
            <div 
                css={(theme) => NavigationButtonStyle(theme, 'left')} 
                onClick={handlePrevious}
                style={{ opacity: activeIndex === 0 ? 0.5 : 1 }}
            >
                <ArrowBackIosNewIcon />
            </div>
            
            <div 
                css={(theme) => NavigationButtonStyle(theme, 'right')} 
                onClick={handleNext}
                style={{ opacity: activeIndex === portfolioImages.length - 1 ? 0.5 : 1 }}
            >
                <ArrowForwardIosIcon />
            </div>
            
            <div css={IndicatorContainerStyle}>
                {portfolioImages.map((_, index) => (
                    <div
                        key={index}
                        css={(theme) => IndicatorStyle(theme, index === activeIndex)}
                        onClick={() => scrollToIndex(index)}
                    />
                ))}
            </div>
        </div>
    );
};